import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, Zap } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

function FeatureRow({ label, note, highlight, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.li
      ref={ref}
      initial={{ opacity: 0, x: -10 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.4, delay }}
      className="flex items-start gap-3"
    >
      <span
        className={`mt-0.5 shrink-0 rounded-full p-0.5 ${
          highlight ? 'bg-[#3D5638]/40 text-[#BDD6B5]' : 'bg-white/5 text-white/40'
        }`}
      >
        <Check size={12} strokeWidth={2.5} />
      </span>
      <span className="text-sm leading-snug">
        <span className={highlight ? 'text-white/90 font-medium' : 'text-white/82'}>{label}</span>
        {note && <span className="block text-xs text-white/30 font-mono mt-0.5">{note}</span>}
      </span>
    </motion.li>
  );
}

export default function PlatformSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const community   = t('platform.community');
  const commercial  = t('platform.commercial');

  return (
    <section id="platform" className="relative pt-20 pb-10 sm:pt-28 sm:pb-14 px-6 sm:px-12 lg:px-24" data-testid="platform-section">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">
            {t('platform.heading')}{' '}
            <span className="text-[#BDD6B5]">{t('platform.headingGreen')}</span>
          </h2>
          <p className="mt-5 text-base text-white/78 max-w-2xl leading-relaxed">
            {t('platform.subtitle')}
          </p>
        </motion.div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">

          {/* ── Community ── */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="glass-card p-8 h-full"
            data-testid="tier-community"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-semibold text-white">{community.name}</h3>
              <span className="font-mono text-xs tracking-widest uppercase text-[#BDD6B5] bg-[#3D5638]/25 px-2.5 py-1 rounded-full border border-[#3D5638]/40">
                {community.badge}
              </span>
            </div>
            <p className="text-sm text-white/68 mb-7 leading-relaxed">{community.tagline}</p>

            <div className="h-px bg-gradient-to-r from-[#3D5638]/50 to-transparent mb-7" />

            <ul className="space-y-4">
              {Array.isArray(community.features) && community.features.map((f, i) => (
                <FeatureRow key={i} label={f} delay={i * 0.06} />
              ))}
            </ul>

            {community.forever && (
              <p className="mt-5 text-xs font-mono tracking-widest uppercase text-[#BDD6B5]/50 border-t border-[#3D5638]/25 pt-5">
                {community.forever}
              </p>
            )}

            <div className="mt-6">
              <a
                href="#community-form"
                className="block w-full text-center border border-[#3D5638]/50 hover:border-[#BDD6B5]/50 text-white/70 hover:text-white rounded-full py-3 text-sm font-medium transition-all duration-300"
              >
                {community.cta}
              </a>
            </div>
          </motion.div>

          {/* ── Commercial Professional ── */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="glass-card p-8 h-full border-[#BDD6B5]/20 relative overflow-hidden"
            data-testid="tier-commercial"
          >
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#BDD6B5]/40 to-transparent" />
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-16 bg-[#3D5638]/15 blur-3xl rounded-full pointer-events-none" />

            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-semibold text-white">{commercial.name}</h3>
              <span className="font-mono text-xs text-white bg-[#3D5638] px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-[0_0_12px_rgba(61,86,56,0.4)] whitespace-nowrap">
                <Zap size={10} strokeWidth={2.5} />
                {commercial.badge}
              </span>
            </div>
            <p className="text-sm text-white/68 mb-7 leading-relaxed">{commercial.tagline}</p>

            <div className="flex items-center gap-3 mb-5">
              <div className="h-px flex-1 bg-gradient-to-r from-[#3D5638]/50 to-transparent" />
              <span className="text-[10px] font-mono tracking-widest uppercase text-white/30 shrink-0">
                {commercial.divider}
              </span>
              <div className="h-px flex-1 bg-gradient-to-l from-[#3D5638]/50 to-transparent" />
            </div>

            <ul className="space-y-4">
              {Array.isArray(commercial.features) && commercial.features.map((f, i) => (
                <FeatureRow
                  key={i}
                  label={f.label}
                  note={f.note}
                  highlight={f.highlight}
                  delay={i * 0.06}
                />
              ))}
            </ul>

            <div className="mt-8">
              <a
                href="#pilot-form"
                className="block w-full text-center bg-[#3D5638] hover:bg-[#4A6845] text-white rounded-full py-3 text-sm font-medium transition-all duration-300 shadow-[0_0_20px_rgba(61,86,56,0.3)] hover:shadow-[0_0_30px_rgba(61,86,56,0.5)]"
              >
                {commercial.cta}
              </a>
              <p className="text-xs text-center text-white/25 mt-3 font-mono">{commercial.note}</p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
